/**
 * Campanella notifiche nella topbar della shell.
 *
 * Bottone stile identico ai bottoni header esistenti (Layout.tsx, cluster menu
 * utente): height 32, bg rgba(255,255,255,0.094), border rgba(255,255,255,0.267).
 * Badge col numero di non lette (nascosto se 0, "99+" oltre 99). Polling del
 * conteggio ogni 60s (pattern EmailLayout.tsx: setInterval + guard
 * document.hidden + cleanup). Click apre un dropdown (stesso pattern ref +
 * click-outside del menu utente in Layout.tsx) con le ultime 10 non lette.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';

/** Notifica cosi' come restituita da GET /api/v1/notifiche (NotificaResponse). */
interface Notifica {
  id: string;
  year: number;
  tipo: string;
  canale: string;
  titolo: string;
  corpo: string | null;
  stato: string;
  priorita: string;
  entita_tipo: string | null;
  entita_id: string | null;
  letta_at: string | null;
  scadenza: string | null;
  meta: { url?: string; [key: string]: unknown } | null;
  created_at: string;
}

const POLL_INTERVAL_MS = 60_000;
const DROPDOWN_LIMIT = 10;

/** Header di autenticazione coerente col resto della shell (token da localStorage). */
const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Formatta una data ISO in formato relativo breve (es. "5 min fa", "2 g fa"). */
function formatDataRelativa(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin} min fa`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH} h fa`;
  const diffG = Math.round(diffH / 24);
  if (diffG < 7) return `${diffG} g fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

const NotificheBell: React.FC = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [notifiche, setNotifiche] = useState<Notifica[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /** Aggiorna il conteggio non lette. Errori silenziosi (no toast a raffica sul polling). */
  const fetchCount = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/notifiche/non-lette/count', {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data: { count: number } = await res.json();
        setCount(data.count);
      }
    } catch {
      // silenzioso: il polling non deve generare rumore
    }
  }, []);

  /** Carica le ultime notifiche non lette per il dropdown. */
  const fetchDropdown = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        stato: 'non_letta',
        limit: String(DROPDOWN_LIMIT),
      });
      const res = await fetch(`/api/v1/notifiche?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        setNotifiche(await res.json());
      }
    } catch {
      // silenzioso
    } finally {
      setLoading(false);
    }
  }, []);

  /* -- Fetch immediato al mount + polling 60s -- */
  useEffect(() => {
    void fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void fetchCount();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [fetchCount]);

  /* -- Dropdown: apertura ricarica la lista, click-outside chiude -- */
  useEffect(() => {
    if (open) void fetchDropdown();
  }, [open, fetchDropdown]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  /** Segna una notifica come letta, aggiorna il conteggio e naviga alla destinazione. */
  const handleClickNotifica = useCallback(
    async (notifica: Notifica) => {
      setOpen(false);
      try {
        await fetch(`/api/v1/notifiche/${notifica.id}/letta?year=${notifica.year}`, {
          method: 'PATCH',
          headers: authHeaders(),
        });
      } catch {
        // silenzioso: si naviga comunque
      }
      void fetchCount();
      navigate(notifica.meta?.url || '/notifiche');
    },
    [navigate, fetchCount],
  );

  /** Segna tutte le notifiche come lette e aggiorna conteggio + lista. */
  const handleSegnaTutteLette = useCallback(async () => {
    try {
      await fetch('/api/v1/notifiche/letta-tutte', {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {
      // silenzioso
    }
    void fetchCount();
    void fetchDropdown();
  }, [fetchCount, fetchDropdown]);

  const handleVediTutte = useCallback(() => {
    setOpen(false);
    navigate('/notifiche');
  }, [navigate]);

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center justify-center transition-colors duration-150"
        style={{
          height: 32,
          width: 32,
          borderRadius: 6,
          backgroundColor: open ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.094)',
          border: '1px solid rgba(255,255,255,0.267)',
          color: '#FFFFFF',
          cursor: 'pointer',
          position: 'relative',
        }}
        title="Notifiche"
      >
        <Bell size={16} />
        {count > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              padding: '0 3px',
              borderRadius: 8,
              backgroundColor: '#DC2626',
              color: '#FFFFFF',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 38,
            right: 0,
            width: 340,
            backgroundColor: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {/* Intestazione */}
          <div
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid var(--color-border)',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--color-foreground)',
            }}
          >
            Notifiche
          </div>

          {/* Lista */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {loading && notifiche.length === 0 && (
              <p
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  fontSize: 12,
                  color: 'var(--color-muted-foreground)',
                }}
              >
                Caricamento...
              </p>
            )}
            {!loading && notifiche.length === 0 && (
              <p
                style={{
                  padding: '16px',
                  textAlign: 'center',
                  fontSize: 12,
                  color: 'var(--color-muted-foreground)',
                }}
              >
                Nessuna notifica non letta
              </p>
            )}
            {notifiche.map((n) => (
              <NotificaRow key={n.id} notifica={n} onClick={() => void handleClickNotifica(n)} />
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              borderTop: '1px solid var(--color-border)',
            }}
          >
            <FooterButton label="Segna tutte lette" onClick={() => void handleSegnaTutteLette()} />
            <div style={{ width: 1, backgroundColor: 'var(--color-border)' }} />
            <FooterButton label="Vedi tutte" onClick={handleVediTutte} />
          </div>
        </div>
      )}
    </div>
  );
};

/* -- Componente interno: riga notifica nel dropdown -- */

interface NotificaRowProps {
  notifica: Notifica;
  onClick: () => void;
}

const NotificaRow: React.FC<NotificaRowProps> = ({ notifica, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
        padding: '10px 16px',
        border: 'none',
        borderBottom: '1px solid var(--color-border)',
        cursor: 'pointer',
        textAlign: 'left',
        backgroundColor: hovered ? 'var(--color-muted, rgba(0,0,0,0.04))' : 'transparent',
        transition: 'background-color 150ms',
      }}
    >
      {/* Pallino priorita' alta */}
      <span
        style={{
          flexShrink: 0,
          marginTop: 5,
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: notifica.priorita === 'alta' ? '#DC2626' : 'transparent',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-foreground)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {notifica.titolo}
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-muted-foreground)', marginTop: 2 }}>
          {formatDataRelativa(notifica.created_at)}
        </div>
      </div>
    </button>
  );
};

/* -- Componente interno: bottone footer dropdown -- */

interface FooterButtonProps {
  label: string;
  onClick: () => void;
}

const FooterButton: React.FC<FooterButtonProps> = ({ label, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1,
        padding: '10px 0',
        fontSize: 12,
        fontWeight: 500,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'center',
        color: 'var(--color-primary, #1B3A6B)',
        backgroundColor: hovered ? 'var(--color-muted, rgba(0,0,0,0.04))' : 'transparent',
        transition: 'background-color 150ms',
      }}
    >
      {label}
    </button>
  );
};

export default NotificheBell;
